import React, { useState, useCallback, useMemo } from 'react';
import { format } from 'date-fns';
import { Send, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { AIQuestionInput } from './AIQuestionInput';
import type { AIQuestionsFormProps, AIQuestion, AIQuestionAnswers } from './types';

/**
 * Format an answer value for display in the chat
 */
function formatAnswerForDisplay(question: AIQuestion, value: unknown): string {
  if (value === null || value === undefined) {
    return '(not answered)';
  }

  switch (question.type) {
    case 'employee_select':
      if (Array.isArray(value)) {
        return `${value.length} employee(s) selected`;
      }
      return 'Employee selected';

    case 'date':
      try {
        return format(new Date(value as string), 'PPP');
      } catch {
        return String(value);
      }

    case 'week':
      try {
        return `Week of ${format(new Date(value as string), 'MMM d, yyyy')}`;
      } catch {
        return String(value);
      }

    case 'month':
      try {
        return format(new Date(value as string + '-01'), 'MMMM yyyy');
      } catch {
        return String(value);
      }

    case 'time_range': {
      const timeValue = value as { startTime: string; endTime: string };
      return `${timeValue.startTime} - ${timeValue.endTime}`;
    }

    case 'single_choice': {
      const option = question.options?.find(o => o.value === value);
      return option?.label || String(value);
    }

    case 'multiple_choice': {
      const values = value as string[];
      const labels = values.map(v => {
        const option = question.options?.find(o => o.value === v);
        return option?.label || v;
      });
      return labels.join(', ');
    }

    case 'yes_no':
      return value === 'yes' ? 'Yes' : 'No';

    case 'number':
    case 'text':
    case 'long_text':
    default:
      return String(value);
  }
}

/**
 * Generate display text from all answers
 */
function generateDisplayText(questions: AIQuestion[], answers: AIQuestionAnswers): string {
  const parts: string[] = [];

  for (const question of questions) {
    const value = answers[question.id];
    if (value !== null && value !== undefined) {
      const answerText = formatAnswerForDisplay(question, value);

      if (questions.length === 1) {
        parts.push(answerText);
      } else {
        const shortQuestion = question.text.length > 30
          ? question.text.substring(0, 30).replace(/\s+\S*$/, '') + '...'
          : question.text;
        parts.push(`${shortQuestion}: ${answerText}`);
      }
    }
  }

  return parts.join('\n');
}

/**
 * AIQuestionsForm - Container for rendering multiple questions with submit button
 */
export const AIQuestionsForm: React.FC<AIQuestionsFormProps> = ({
  questions,
  businessId,
  onSubmit,
  disabled = false,
}) => {
  const [answers, setAnswers] = useState<AIQuestionAnswers>(() => {
    const initial: AIQuestionAnswers = {};
    for (const q of questions) {
      initial[q.id] = null;
    }
    return initial;
  });

  const [isSubmitted, setIsSubmitted] = useState(false);

  const validationErrors = useMemo(() => {
    const errors: Record<string, string> = {};

    for (const question of questions) {
      if (question.required) {
        const value = answers[question.id];
        const isEmpty =
          value === null ||
          value === undefined ||
          value === '' ||
          (Array.isArray(value) && value.length === 0);

        if (isEmpty) {
          errors[question.id] = 'This field is required';
        }
      }
    }

    return errors;
  }, [questions, answers]);

  const hasAnyAnswer = Object.values(answers).some(
    v => v !== null && v !== undefined && v !== '' && (!Array.isArray(v) || v.length > 0)
  );

  const handleAnswerChange = useCallback((questionId: string, value: unknown) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: value,
    }));
  }, []);

  const handleSubmit = useCallback(() => {
    if (!hasAnyAnswer) return;

    const displayText = generateDisplayText(questions, answers);
    setIsSubmitted(true);
    onSubmit(answers, displayText);
  }, [questions, answers, hasAnyAnswer, onSubmit]);

  if (isSubmitted) {
    return (
      <div className="text-xs text-muted-foreground italic py-1">
        Response submitted
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {questions.map((question) => (
        <div key={question.id} className="relative">
          <AIQuestionInput
            question={question}
            businessId={businessId}
            value={answers[question.id]}
            onChange={(value) => handleAnswerChange(question.id, value)}
            disabled={disabled || isSubmitted}
          />
          {question.required && (
            <span className="absolute top-0 right-0 text-xs text-red-500">*</span>
          )}
          {validationErrors[question.id] && hasAnyAnswer && (
            <div className="flex items-center gap-1 mt-1 text-xs text-red-500">
              <AlertCircle className="h-3 w-3" />
              {validationErrors[question.id]}
            </div>
          )}
        </div>
      ))}

      <div className="flex justify-end pt-1">
        <Button
          size="sm"
          onClick={handleSubmit}
          disabled={disabled || !hasAnyAnswer || isSubmitted}
          className="h-7 text-xs gap-1"
        >
          <Send className="h-3 w-3" />
          Send Answer{questions.length > 1 ? 's' : ''}
        </Button>
      </div>

      <p className="text-[10px] text-muted-foreground">
        You can also type your answer directly in the chat input below
      </p>
    </div>
  );
};

export default AIQuestionsForm;
